import { useEffect, useMemo, useState } from "react";
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MainLayout from "../layout/MainLayout";
import { useAuth } from "../context/AuthContext";
import { useAlertContext } from "../context/AlertContext";
import { useMenuPrefs } from "../context/MenuPrefsContext";
import {
    DEFAULT_MENU_LAYOUT,
    EMPTY_MENU_PREFS,
    MENU_VERSION,
    isNavGroup,
    menuLayoutOf,
    normalizeMenuPrefs,
    personalMenuLayout,
    startPageOptions,
} from "../config/navConfig";
import * as Icons from "../components/common/Icons";

/**
 * «Настройка меню» — правка №5, №52. [Claude] 2026-11-02 / 2026-11-03.
 * Each user: order of menu items (drag or arrows), show/hide, order inside
 * groups at any level (groups stay groups), start page, submenu mode
 * («сверху» — как в Битрикс24, «слева»). Saved on the server for the account.
 * Hiding only changes the menu; access rights stay as the role allows.
 */

function toDraft(layout) {
    return layout.map((item) => ({
        key: item.key,
        label: item.label,
        icon: item.icon,
        visible: item.visible,
        children: isNavGroup(item) ? toDraft(item.children) : null,
    }));
}

export function draftToPrefs(draft, home, extra = {}) {
    const hidden = [];
    const children = {};
    const walk = (items) => items.forEach((item) => {
        if (!item.visible) hidden.push(item.key);
        if (item.children) {
            children[item.key] = item.children.map((c) => c.key);
            walk(item.children);
        }
    });
    walk(draft);
    return normalizeMenuPrefs({
        order: draft.map((i) => i.key),
        hidden,
        children,
        home: home || null,
        version: MENU_VERSION,
        layout: extra.layout || null,
        open: extra.open || [],
    });
}

function hasVisibleItem(draft) {
    return draft.some((item) => item.visible && (!item.children || hasVisibleItem(item.children)));
}

/** Заменить список детей группы `key` (на любом уровне); key=null — верхний уровень. */
function mapList(draft, key, fn) {
    if (key === null) return fn(draft);
    return draft.map((item) => {
        if (!item.children) return item;
        if (item.key === key) return { ...item, children: fn(item.children) };
        return { ...item, children: mapList(item.children, key, fn) };
    });
}

function setVisibleDeep(draft, key, visible) {
    return draft.map((item) => {
        if (item.key === key) return { ...item, visible };
        return item.children ? { ...item, children: setVisibleDeep(item.children, key, visible) } : item;
    });
}

function MoveButtons({ index, count, onMove, label }) {
    return (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={() => onMove(index, index - 1)}
                disabled={index === 0}
                className="w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                aria-label={`Поднять «${label}»`}
            >
                ↑
            </button>
            <button
                type="button"
                onClick={() => onMove(index, index + 1)}
                disabled={index === count - 1}
                className="w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                aria-label={`Опустить «${label}»`}
            >
                ↓
            </button>
        </div>
    );
}

function VisibleToggle({ checked, onChange, label }) {
    return (
        <label className="flex items-center gap-2 text-xs text-slate-500 select-none cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="w-4 h-4 accent-[#e0bb48]"
                aria-label={`Показывать «${label}»`}
            />
            показывать
        </label>
    );
}

function SortableRow({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    return (
        <li
            ref={setNodeRef}
            style={style}
            className={`rounded-xl border bg-white ${isDragging ? "border-[#e0bb48] shadow-md z-10 relative" : "border-slate-200"}`}
        >
            <div className="flex items-start gap-2 p-2">
                <button
                    type="button"
                    className="mt-1 px-1.5 text-slate-400 cursor-grab active:cursor-grabbing touch-none"
                    aria-label="Перетащить"
                    {...attributes}
                    {...listeners}
                >
                    ⋮⋮
                </button>
                <div className="flex-1 min-w-0">{children}</div>
            </div>
        </li>
    );
}

function SortableList({ ids, onReorder, children }) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        onReorder(ids.indexOf(active.id), ids.indexOf(over.id));
    };
    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
        </DndContext>
    );
}

/** Список пунктов одного уровня; группы рисуют свой вложенный список. */
function Rows({ items, parentKey, depth, parentVisible, onMove, onVisible }) {
    const move = (from, to) => onMove(parentKey, from, to);
    return (
        <SortableList ids={items.map((i) => i.key)} onReorder={move}>
            <ul
                className={depth === 0 ? "space-y-2" : "mt-2 ml-6 space-y-1.5"}
                data-testid={depth === 0 ? "menu-settings-list" : `menu-settings-group-${parentKey}`}
            >
                {items.map((item, index) => {
                    const Icon = Icons[item.icon] || Icons.FileTextIcon;
                    const shown = item.visible && parentVisible;
                    return (
                        <SortableRow key={item.key} id={item.key}>
                            <div className="flex items-center gap-3">
                                {depth === 0 && <Icon className={`w-5 h-5 ${shown ? "text-slate-600" : "text-slate-300"}`} />}
                                <span
                                    className={`flex-1 text-sm ${depth === 0 || item.children ? "font-medium" : ""} ${shown ? "text-slate-800" : "text-slate-400 line-through"}`}
                                    data-testid={depth === 0 ? "menu-settings-item" : "menu-settings-child"}
                                >
                                    {item.label}
                                    {item.children && <span className="ml-1 text-xs font-normal text-slate-400">· группа</span>}
                                </span>
                                <VisibleToggle checked={item.visible} onChange={(v) => onVisible(item.key, v)} label={item.label} />
                                <MoveButtons index={index} count={items.length} onMove={move} label={item.label} />
                            </div>
                            {item.children && (
                                <Rows
                                    items={item.children}
                                    parentKey={item.key}
                                    depth={depth + 1}
                                    parentVisible={shown}
                                    onMove={onMove}
                                    onVisible={onVisible}
                                />
                            )}
                        </SortableRow>
                    );
                })}
            </ul>
        </SortableList>
    );
}

const LAYOUT_OPTIONS = [
    ["top", "Сверху строкой — как в Битрикс24", "Слева только разделы и группы; разделы открытой группы — строкой над страницей."],
    ["side", "Слева списком", "Группы раскрываются в левом меню, вложенные — внутри них."],
];

export default function MenuSettingsPage() {
    const { user } = useAuth();
    const { showAlert } = useAlertContext();
    const { prefs, save } = useMenuPrefs();
    const role = user?.role;

    const savedPrefs = useMemo(() => normalizeMenuPrefs(prefs), [prefs]);
    const [draft, setDraft] = useState(() => toDraft(personalMenuLayout(role, savedPrefs)));
    const [home, setHome] = useState(savedPrefs.home || "");
    const [layout, setLayout] = useState(menuLayoutOf(savedPrefs));
    const [saving, setSaving] = useState(false);

    // Settings arrived from the server after the page opened — show them.
    useEffect(() => {
        setDraft(toDraft(personalMenuLayout(role, savedPrefs)));
        setHome(savedPrefs.home || "");
        setLayout(menuLayoutOf(savedPrefs));
    }, [role, savedPrefs]);

    const options = useMemo(() => startPageOptions(role), [role]);
    const extra = { layout, open: savedPrefs.open };
    const nextPrefs = draftToPrefs(draft, home, extra);
    const savedAsDraft = draftToPrefs(toDraft(personalMenuLayout(role, savedPrefs)), savedPrefs.home, {
        layout: menuLayoutOf(savedPrefs),
        open: savedPrefs.open,
    });
    const dirty = JSON.stringify(nextPrefs) !== JSON.stringify(savedAsDraft);

    const onMove = (parentKey, from, to) => {
        setDraft((d) => mapList(d, parentKey, (list) => (to < 0 || to >= list.length ? list : arrayMove(list, from, to))));
    };
    const onVisible = (key, visible) => setDraft((d) => setVisibleDeep(d, key, visible));

    const resetDefaults = () => {
        setDraft(toDraft(personalMenuLayout(role, EMPTY_MENU_PREFS)));
        setHome("");
        setLayout(DEFAULT_MENU_LAYOUT);
    };

    const onSave = async () => {
        if (!hasVisibleItem(draft)) {
            showAlert("Оставьте в меню хотя бы один раздел", "error");
            return;
        }
        setSaving(true);
        try {
            await save(nextPrefs);
            showAlert("Настройки меню сохранены", "success");
        } catch (e) {
            showAlert(`Не удалось сохранить: ${e?.message || e}`, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <MainLayout>
            <div className="max-w-3xl">
                <div className="mb-5">
                    <h1 className="text-2xl font-bold text-slate-900">Настройка меню</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Порядок разделов, видимость, вид подменю и стартовая страница — только для вашей учётной записи.
                        Скрытый раздел пропадает из меню, но доступ к нему не меняется.
                    </p>
                </div>

                <div className="mb-5 grid gap-3 md:grid-cols-2">
                    <section className="rounded-2xl border border-slate-200 bg-white p-4">
                        <h2 className="text-sm font-semibold text-slate-800 mb-2">Разделы групп показывать</h2>
                        <div className="space-y-2" role="radiogroup" aria-label="Разделы групп показывать">
                            {LAYOUT_OPTIONS.map(([value, label, hint]) => (
                                <label key={value} className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="menu-layout"
                                        value={value}
                                        checked={layout === value}
                                        onChange={() => setLayout(value)}
                                        className="mt-1 accent-[#e0bb48]"
                                        aria-label={label}
                                    />
                                    <span>
                                        <span className="block text-sm text-slate-800">{label}</span>
                                        <span className="block text-xs text-slate-500">{hint}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </section>

                    <section className="rounded-2xl border border-slate-200 bg-white p-4">
                        <label htmlFor="menu-start-page" className="block text-sm font-semibold text-slate-800 mb-2">
                            Открывать при входе
                        </label>
                        <select
                            id="menu-start-page"
                            value={home}
                            onChange={(e) => setHome(e.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
                        >
                            <option value="">Первый раздел моего меню</option>
                            {options.map((o) => (
                                <option key={o.to} value={o.to}>{o.label}</option>
                            ))}
                        </select>
                    </section>
                </div>

                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <h2 className="text-sm font-semibold text-slate-800 mb-1">Разделы меню</h2>
                    <p className="text-xs text-slate-500 mb-3">Перетащите за ⋮⋮ или используйте стрелки. Внутри групп порядок меняется так же.</p>
                    <Rows items={draft} parentKey={null} depth={0} parentVisible onMove={onMove} onVisible={onVisible} />
                </section>

                <div className="sticky bottom-0 mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-slate-50/95 py-3">
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={saving || !dirty}
                        className="px-5 py-2.5 bg-[#e0bb48] text-black rounded-xl hover:bg-[#d4af3a] text-sm font-medium shadow-sm disabled:opacity-50"
                    >
                        {saving ? "Сохранение…" : "Сохранить"}
                    </button>
                    <button
                        type="button"
                        onClick={resetDefaults}
                        disabled={saving}
                        className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-100 text-sm font-medium"
                    >
                        Сбросить как было
                    </button>
                    {dirty && <span className="text-xs text-slate-500">Есть несохранённые изменения</span>}
                </div>
            </div>
        </MainLayout>
    );
}
